import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Content, Template } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { TemplatePreview } from "../template/shared/TemplatePreview";
import { extractDynamicTextFromCanvasData } from "@/components/canvas/utils/canvasUtils";
import { useEffect, useState } from "react";
import { CanvasData } from "@/components/canvas/types/canvasTypes";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { cn } from "@/lib/utils";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { CreateFromTemplateDynamicTextsForm } from "./CreateFromTemplateDynamicTextsForm";
import { bindDataSourceToTemplate } from "../template/utils/templateUtils";
import { useGenerateAndUpload } from "../contentCreateFromTemplate/hooks/useGenerateAndUpload";

interface Props {
  template: Template;
  content: Content;
  onClose: () => void;
  onUploadSuccess: () => void;
}

export const UpdateContentVariablesDialog: React.FC<Props> = ({ template, content, onClose, onUploadSuccess }) => {
  const { t } = useTranslation();
  const canvasData = template.canvas_data;
  const dynamicTexts = extractDynamicTextFromCanvasData(canvasData);
  const [newCanvasData, setNewCanvasData] = useState<CanvasData>(canvasData);

  useEffect(() => {
    setNewCanvasData(canvasData);
  }, [setNewCanvasData, canvasData]);

  const { generateAndUpdate, isUpdating } = useGenerateAndUpload({
    newCanvasData,
    template,
    onUploadSuccess,
    // @ts-ignore
    contentProps: {},
    contentGuid: content.contentGuid,
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("content.createFromTemplate")}:{" "}
            <span className="underline">
              {template.name} ({template.viewport_size})
            </span>
          </DialogTitle>
          <DialogDescription>{t("content.createFromTemplateDesc")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-start space-x-4">
          <div className="w-1/3">
            <div className={cn(`rounded-md overflow-hidden border h-[500px]`, getCheckeredBg())}>
              <TemplatePreview data={newCanvasData} previewSize="full" />
            </div>
          </div>
          <div className="w-2/3">
            <div className="space-y-4">
              {dynamicTexts.length > 0 && (
                <CreateFromTemplateDynamicTextsForm
                  dynamicTexts={dynamicTexts}
                  onFormChange={(dataSourceManual) => {
                    setNewCanvasData(bindDataSourceToTemplate(template, dataSourceManual).canvas_data);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button disabled={isUpdating} onClick={onClose} variant="outline">
            {t("common.cancel")}
          </Button>
          <Button onClick={generateAndUpdate} disabled={isUpdating}>
            {t("content.updateImage")} {isUpdating && <LoadingSmall />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
